import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, BellOff } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 60 * 1000; // check every minute

/**
 * Play a soft two-note chime using the Web Audio API. No external asset is
 * needed. Browsers block audio autoplay before any user gesture, so if that
 * happens we swallow the error silently — user will get audio on next
 * gesture (opening the dialog counts).
 */
function playChime() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const now = ctx.currentTime;
    // Two-note chime C5 → E5 (soft, 0.35s total)
    [523.25, 659.25].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const start = now + i * 0.18;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.22, start + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.35);
      osc.connect(gain).connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.4);
    });
    setTimeout(() => ctx.close(), 900);
  } catch {
    /* ignore — no audio */
  }
}

/**
 * Global modal that pops up when the current business needs to close its
 * cash register. Fires exactly at the closing hour set in /configuracion.
 *
 * The alert works even without an active session because it hits a public
 * endpoint (`/api/public/cash-closing-status`) and reads the last-selected
 * business id from localStorage (`preSelectedBusinessId`). It stays visible
 * as long as the cash register is open past the closing time — closing the
 * register (from /caja) makes it disappear automatically on the next poll.
 *
 * Additional UX:
 *   • Plays a soft chime once each time the modal transitions from hidden
 *     → visible (per business, per snooze cycle).
 *   • The user can enter a personalized number of minutes to snooze the
 *     alert; it re-appears automatically when the snooze window elapses
 *     (still only if the register is still open past close). Snooze is
 *     persisted in localStorage per business.
 */
export default function CashClosingAlert() {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [snoozeMinutes, setSnoozeMinutes] = useState(15);
  const [businessId, setBusinessId] = useState(() => 'main');
  const wasOpenRef = useRef(false);

  const snoozeKey = businessId ? `cashAlertSnoozeUntil_${businessId}` : null;

  const check = useCallback(async () => {
    const bid = 'main';
    setBusinessId(bid);
    if (!bid) {
      setStatus(null); setOpen(false); return;
    }
    try {
      const { data } = await axios.get(`${API}/public/cash-closing-status`, {
        params: { business_id: bid },
      });
      setStatus(data);
      const snoozeUntil = Number(localStorage.getItem(`cashAlertSnoozeUntil_${bid}`) || 0);
      const isSnoozed = snoozeUntil > Date.now();
      setOpen(!!data.needs_closing && !isSnoozed);
    } catch {
      setStatus(null); setOpen(false);
    }
  }, []);

  useEffect(() => {
    check();
    const id = setInterval(check, POLL_MS);
    return () => clearInterval(id);
  }, [check]);

  // Also re-check on route change so navigating to /caja closes the alert
  // faster than the 60s poll after actually closing the register.
  useEffect(() => { check(); }, [location.pathname, check]);

  // Play the chime exactly on the transition hidden → visible.
  useEffect(() => {
    if (open && !wasOpenRef.current) playChime();
    wasOpenRef.current = open;
  }, [open]);

  if (!open || !status) return null;

  const goToCaja = () => {
    setOpen(false);
    navigate('/caja');
  };

  const applySnoozeWith = (value) => {
    const mins = Math.max(1, Math.min(240, parseInt(value, 10) || 0));
    if (!snoozeKey) return;
    const until = Date.now() + mins * 60 * 1000;
    localStorage.setItem(snoozeKey, String(until));
    setOpen(false);
    toast.success(`Alerta pospuesta ${mins} min. Reaparece a las ${new Date(until).toLocaleTimeString('es-MX', { timeZone: 'America/Mexico_City', hour: '2-digit', minute: '2-digit' })}`);
  };

  return (
    <Dialog open={open} onOpenChange={() => { /* not dismissible except via buttons */ }}>
      <DialogContent
        className="bg-white max-w-md border-2 border-amber-300 [&>button.absolute.right-4]:hidden"
        data-testid="cash-closing-alert-dialog"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-900">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            Hora de cierre de caja
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            {status.business_name || 'El negocio'} cerró a las{' '}
            <strong className="text-slate-800">{status.closing_time}</strong> y hay una caja abierta.
          </DialogDescription>
        </DialogHeader>

        <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-900 space-y-1">
          <div className="flex justify-between">
            <span>Hora actual (CDMX)</span>
            <span className="font-mono font-bold" data-testid="cash-closing-now">{status.now}</span>
          </div>
          <div className="flex justify-between">
            <span>Hora de cierre</span>
            <span className="font-mono font-bold">{status.closing_time}</span>
          </div>
        </div>

        <div className="rounded-md border border-slate-200 p-3 space-y-2">
          <label className="text-xs font-medium text-slate-600 flex items-center gap-1">
            <BellOff className="w-3.5 h-3.5" />
            Posponer alerta
          </label>
          <div className="flex items-center gap-1.5">
            {[5, 15, 30].map((m) => (
              <Button
                key={m}
                type="button"
                onClick={() => { setSnoozeMinutes(m); applySnoozeWith(m); }}
                variant="outline"
                size="sm"
                className="border-slate-200 h-8 px-2 text-xs"
                data-testid={`cash-closing-snooze-preset-${m}`}
              >
                {m} min
              </Button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={1}
              max={240}
              value={snoozeMinutes}
              onChange={(e) => setSnoozeMinutes(e.target.value)}
              className="w-24 bg-slate-100 border-slate-200"
              data-testid="cash-closing-snooze-input"
            />
            <span className="text-sm text-slate-500">minutos</span>
            <Button
              onClick={() => applySnoozeWith(snoozeMinutes)}
              variant="outline"
              size="sm"
              className="ml-auto border-slate-200"
              data-testid="cash-closing-snooze-btn"
            >
              Posponer
            </Button>
          </div>
          <p className="text-[10px] text-slate-400">
            La alerta reaparecerá automáticamente si la caja sigue abierta.
          </p>
        </div>

        <DialogFooter>
          <Button
            onClick={goToCaja}
            className="bg-amber-600 text-white hover:bg-amber-700 w-full"
            data-testid="cash-closing-alert-go-btn"
          >
            Ir a Caja
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
